import React, { useEffect, useRef, useState, useMemo } from "react";
import { getApi } from "../../../Services/apiService";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardDeceasedMember,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";

function AgeByGroup() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [legendLines, setLegendLines] = useState([]);
  const [agesMilestone, setAgesMilestone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupedMilestones, setGroupedMilestones] = useState([]);
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [headOfLineColumnWidth, setHeadOfLineColumnWidth] = useState("auto");
  const [headOfLineNameWidth, setHeadOfLineNameWidth] = useState("auto");
  const [sortBy, setSortBy] = useState("youngest"); // "none", "oldest", "youngest", "alpha-asc", "alpha-desc", "color", "death-asc"
  const [searchQuery, setSearchQuery] = useState("");
  // The column header (Ages … Grp Totals): the search box and sorts centre on it.
  const columnHeaderRef = useRef(null);
  const [isLargeView, setIsLargeView] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Age groups expected from API:
  // The API returns age_by_group with groups (e.g., 80, etc.)
  // The API already groups data by these age groups, so no calculation needed on frontend

  // Fetch age by group data
  useEffect(() => {
    const fetchAgesMilestone = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setAgesMilestone([]);
          setGroupedMilestones([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          const allMembers = extractAllMembersFromStatisticsResponse(response.data);
          const heads = buildHeadsOfLineIdByColorMap(
            extractHeadsOfLineFromStatisticsResponse(response.data),
            allMembers
          );
          setHeadsMap(heads);
          // The phone's footer key. Built from the whole tree, not this page's
          // rows, so it lists the same family lines as every other detail page.
          setLegendLines(buildDashboardColorLegendLines(allMembers, heads));
          // Extract age by group data from the response
          // The API returns: data.age_by_group.details (array of age groups)
          // Each group has: { group: number, count: number, details: [array of people] }
          const ageByGroupData = response?.data?.age_by_group?.details || [];
          
          // Ensure it's always an array
          if (Array.isArray(ageByGroupData)) {
            // Flatten all people from all age groups for total count
            const allPeople = ageByGroupData.flatMap(group => group?.details || []);
            setAgesMilestone(allPeople);
            
            // Store grouped data for rendering
            // Sort by group in descending order (oldest first)
            const sortedGroups = ageByGroupData
              .filter(group => group?.group && group?.details?.length > 0)
              .sort((a, b) => b.group - a.group);
            
            setGroupedMilestones(sortedGroups);
          } else {
            console.warn("Age by group data is not an array:", ageByGroupData);
            setAgesMilestone([]);
            setGroupedMilestones([]);
          }
        } else {
          setAgesMilestone([]);
          setGroupedMilestones([]);
        }
      } catch (error) {
        console.error("Error fetching age by group data:", error);
        setAgesMilestone([]);
        setGroupedMilestones([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAgesMilestone();
  }, []);

  const formatPersonName = (member) => formatDashboardMemberDisplayName(member);

  // Helper function to render member row with 2 columns
  const renderMemberRow = (member) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member);
    const showInLawAsterisk =
      isDashboardInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.family_line_hex_code || member?.hex_code || "#000000";
    const colorName = member?.family_line_color || member?.color_name || "";

    return (
      <div className={`ages-milestone-member-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Person Name */}
        <div className={`ages-milestone-member-name ${isLargeView ? 'large-view' : ''}`} style={{ minWidth: nameColumnWidth }}>
          <DashboardNameMarkerGroup>
          <DashboardPhotoName
            {...getDashboardMemberNamePhotoUrls(member)}
            onViewPhoto={openPhoto}
          >
            <DashboardMemberNameLabel
              baseName={baseName}
              descendantSpouseName={descendantSpouseName}
              afterBaseName={
                showInLawAsterisk && descendantSpouseName ? (
                  <DashboardInLawAsterisk isLargeView={isLargeView} />
                ) : null
              }
            />
          </DashboardPhotoName>
          {showInLawAsterisk && !descendantSpouseName && (
            <DashboardInLawAsterisk isLargeView={isLargeView} />
          )}
          {isDashboardDeceasedMember(member) && (
            <DashboardDeceasedAngel isLargeView={isLargeView} />
          )}
          {multipleBirthSup && (
            <sup className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}>{multipleBirthSup}</sup>
          )}
          </DashboardNameMarkerGroup>
        </div>
        
        {/* Column 2: Head of Line Name and Color. Rendered on both layouts — the
            compact one scrolls sideways to reach it rather than dropping it. */}
        <div
          className={`ages-milestone-member-head ${isLargeView ? 'large-view' : ''}`}
          style={{ width: headOfLineColumnWidth, "--head-of-line-name-width": headOfLineNameWidth }}
        >
          <div className={isLargeView ? 'large-view-text' : ''}>
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
        </div>
      </div>
    );
  };

  // Measure the actual width of name and head of line elements after render
  useEffect(() => {
    if (agesMilestone.length === 0 || loading) return;
    
    // Helper functions (local copies for useEffect)
    const getHeadOfLineNameLocal = (row) =>
      getDashboardFamilyLineHeadDisplayName(row, headsMap);
    
    // Use appropriate font size based on view mode
    const fontSize = isLargeView ? '24px' : '16px';
    const nameColBuffer = 8;
    
    // Create temporary elements for measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = 'inherit';
    tempDiv.style.display = 'flex';
    tempDiv.style.alignItems = 'center';
    document.body.appendChild(tempDiv);
    
    const chMeasure = document.createElement('div');
    chMeasure.style.position = 'absolute';
    chMeasure.style.visibility = 'hidden';
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = 'inherit';
    chMeasure.textContent = '0';
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.offsetWidth;

    // Lead-in on the name: 20px on the wide layout, none on the compact one,
    // where the column runs flush to the left margin (see the matching
    // `padding-left` in the media block). Measure it the way it renders, or the
    // longest name outgrows the column's min-width and widens just that row.
    const nameColPaddingLeft = isCompactLayout ? 0 : 20;

    let maxNameWidth = 0;
    let maxHeadOfLineWidth = 0;
    let maxHeadNameWidth = 0;
    
    agesMilestone.forEach((member) => {
      const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member);
      const supText = getMultipleBirthSuperscriptText(member);
      const showInLawAsterisk =
        isDashboardInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);

      tempDiv.innerHTML = "";
      tempDiv.style.paddingLeft = "0";

      const nameSpan = document.createElement("span");
      nameSpan.style.paddingLeft = `${nameColPaddingLeft}px`;
      nameSpan.textContent = baseName;
      if (descendantSpouseName) {
        nameSpan.appendChild(document.createTextNode(" ("));
        const strong = document.createElement("strong");
        strong.className = "dashboard-descendant-spouse-name";
        strong.textContent = descendantSpouseName;
        nameSpan.appendChild(strong);
        nameSpan.appendChild(document.createTextNode(")"));
      }
      tempDiv.appendChild(nameSpan);

      if (supText) {
        const sup = document.createElement("sup");
        sup.className = "dashboard-multiple-birth-sup";
        sup.textContent = supText;
        sup.style.fontSize = "0.75em";
        sup.style.fontWeight = "700";
        tempDiv.appendChild(sup);
      }

      if (showInLawAsterisk) {
        const ast = document.createElement("span");
        ast.className = "dashboard-in-law-asterisk";
        ast.textContent = "*";
        ast.style.fontWeight = isLargeView ? "700" : "400";
        tempDiv.appendChild(ast);
      }

      // The angel is a fixed-size image (see DashboardDeceasedAngel), so a box
      // of the same size stands in for it.
      if (isDashboardDeceasedMember(member)) {
        const angel = document.createElement("span");
        angel.style.display = "inline-block";
        angel.style.width = isLargeView ? "18px" : "12px";
        angel.style.flexShrink = "0";
        tempDiv.appendChild(angel);
      }

      const nameWidth = tempDiv.offsetWidth;
      if (nameWidth > maxNameWidth) {
        maxNameWidth = nameWidth;
      }
      
      // Measure head of line width
      const headOfLineName = getHeadOfLineNameLocal(member) || "Start/Top of Line";
      const colorName = member?.family_line_color || member?.color_name || "";
      const colorText = colorName ? ` (●  ${colorName} Line)` : "";
      const headOfLineText = `${headOfLineName}${colorText}`;
      tempDiv.style.display = "block";

      // Measure the head-of-line name on its own so the color dot/label align
      // vertically across rows regardless of name length.
      tempDiv.textContent = headOfLineName;
      const headNameWidth = tempDiv.offsetWidth;
      if (headNameWidth > maxHeadNameWidth) {
        maxHeadNameWidth = headNameWidth;
      }

      tempDiv.textContent = headOfLineText;
      const headOfLineWidth = tempDiv.offsetWidth;
      if (headOfLineWidth > maxHeadOfLineWidth) {
        maxHeadOfLineWidth = headOfLineWidth;
      }

      tempDiv.style.display = "flex";
    });
    
    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    
    const headOfLineWidthInCh = Math.ceil(maxHeadOfLineWidth / chWidth);
    setNameColumnWidth(`${maxNameWidth + nameColBuffer}px`);
    setHeadOfLineColumnWidth(`${headOfLineWidthInCh}ch`);
    setHeadOfLineNameWidth(`${Math.ceil(maxHeadNameWidth)}px`);
  }, [agesMilestone, loading, isLargeView, isCompactLayout]);

  const getMemberLineColor = (member) =>
    (member?.family_line_color || member?.color_name || "").toLowerCase();

  // The search box narrows each group's members, and a group nobody in it
  // matches drops out with the rest of the empty ones.
  const filteredGroupedMilestones = useMemo(() => {
    if (!groupedMilestones.length) return [];
    return groupedMilestones
      .map((group) => ({
        ...group,
        details: filterDashboardMembersBySearch(
          group.details || [],
          searchQuery,
          formatPersonName
        ),
      }))
      .filter((group) => group.details.length > 0);
  }, [groupedMilestones, searchQuery]);

  const isSearching = searchQuery.trim().length > 0;

  const filteredTotal = useMemo(
    () =>
      filteredGroupedMilestones.reduce(
        (sum, group) => sum + (group.details?.length || 0),
        0
      ),
    [filteredGroupedMilestones]
  );

  // Sort grouped milestones based on sortBy
  const sortedGroupedMilestones = useMemo(() => {
    if (!filteredGroupedMilestones.length) return [];
    
    // Create deep copy to avoid mutating original
    const sorted = filteredGroupedMilestones.map(group => ({
      ...group,
      details: group.details ? [...group.details] : []
    }));
    
    // Sort groups by age group (for oldest/youngest, or default to oldest for alpha)
    if (
      sortBy === "oldest" ||
      sortBy === "alpha-asc" ||
      sortBy === "alpha-desc" ||
      sortBy === "color" ||
      sortBy === "death-asc" ||
      sortBy === "none"
    ) {
      // Sort by group in descending order (oldest first)
      sorted.sort((a, b) => b.group - a.group);
    } else if (sortBy === "youngest") {
      // Sort by group in ascending order (youngest first)
      sorted.sort((a, b) => a.group - b.group);
    }
    
    // Sort members within each group based on sortBy
    sorted.forEach((group) => {
      if (group?.details && Array.isArray(group.details)) {
        if (sortBy === "death-asc") {
          group.details = sortDashboardMembersByDeathDate(group.details);
        } else if (sortBy === "alpha-asc") {
          group.details.sort((a, b) => {
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
        } else if (sortBy === "alpha-desc") {
          group.details.sort((a, b) => {
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameB.localeCompare(nameA);
          });
        } else if (sortBy === "oldest") {
          group.details.sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);
            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobA - dobB;
          });
        } else if (sortBy === "youngest") {
          group.details.sort((a, b) => {
            const dobA = getMemberDobTimestamp(a);
            const dobB = getMemberDobTimestamp(b);
            if (dobA === -1 && dobB === -1) return 0;
            if (dobA === -1) return 1;
            if (dobB === -1) return -1;
            return dobB - dobA;
          });
        } else if (sortBy === "color") {
          group.details.sort((a, b) => {
            const colorA = getMemberLineColor(a);
            const colorB = getMemberLineColor(b);
            if (colorA !== colorB) {
              return colorA.localeCompare(colorB);
            }
            const nameA = formatPersonName(a).toLowerCase();
            const nameB = formatPersonName(b).toLowerCase();
            return nameA.localeCompare(nameB);
          });
        }
      }
    });
    
    return sorted;
  }, [filteredGroupedMilestones, sortBy]);

  return (
    <div className={`dashboard-container ages-milestone-container current-year-decade-ages-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box ages-milestone-box dashboard-detail-box">
        <div className="ages-milestone-header">
          <div className="ages-milestone-header-left">
            <DashboardBackButton />
            <h3 className="ages-milestone-title">Decade Ages this Year</h3>
            <div className="ages-milestone-count">
              {filteredTotal}
            </div>
            {!loading && agesMilestone.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                centreOnRef={columnHeaderRef}
              />
            )}
            {!loading && agesMilestone.length > 0 && (
              <DashboardDetailSortFilterBar
                className="ages-milestone-sort-toggle"
                buttonClassName="ages-milestone-sort-btn"
                sortBy={sortBy}
                onSortChange={setSortBy}
                defaultSort="youngest"
                showMemberFilters={false}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="ages-milestone-empty-state">
            <p>Loading age by group data...</p>
          </div>
        ) : agesMilestone.length === 0 ? (
          <div className="ages-milestone-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : filteredTotal === 0 ? (
          <div className="ages-milestone-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <>
            {/* Compact layout scrolls sideways once the head-of-line column pushes
                rows past the card. The column header rides inside the scroller so
                "Grp Totals" stays over its badge instead of decoupling from it.
                Both wrappers are `display: contents` above the breakpoint. */}
            <div className="ages-milestone-hscroll">
            <div className="ages-milestone-hscroll-track">
            <div ref={columnHeaderRef} className="ages-milestone-column-header">
              <span className="ages-milestone-column-label">Ages</span>
              <span className="ages-milestone-column-header-spacer" aria-hidden="true" />
              <span className="ages-milestone-column-label ages-milestone-column-label-totals">Grp Totals</span>
            </div>
            <div className="ages-milestone-list">
            {sortedGroupedMilestones.map((ageGroup) => {
              const group = ageGroup?.group;
              const members = ageGroup?.details || [];

              return (
                <div key={group} className="ages-milestone-group">
                  {/* Age Group Header */}
                  <div className="ages-milestone-group-header">
                    <span className="ages-milestone-badge">
                      {group}
                    </span>
                    <div className="ages-milestone-divider"></div>
                    <span className="ages-milestone-count-badge">
                      {members.length}
                    </span>
                  </div>

                  {/* Members List - Centered between badges */}
                  <div className="ages-milestone-members-container">
                    <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
                      {group}
                    </span>
                    <div className="ages-milestone-members-list">
                      {members.map((member, index) => (
                        <div key={member?.id || index}>
                          {renderMemberRow(member)}
                        </div>
                      ))}
                    </div>
                    <span className="ages-milestone-spacer-badge" style={{ visibility: 'hidden' }}>
                      {members.length}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          </div>
          </div>
            {/* Marker legend keyed to the rows on screen, as on the other detail
                pages, then the phone's family-lines key (as on Family Members). */}
            <div className="ages-milestone-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  sortedGroupedMilestones.map((group) => group.details)
                )}
              />
              {isCompactLayout && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default AgeByGroup;

